import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, useFieldArray } from 'react-hook-form'
import { object, string, date, boolean, number, array } from 'yup'
import { isNil } from 'lodash'
import { formatISO, isValid, parseISO } from 'date-fns'
import { type Sweepstake, type SweepstakeStatusType } from 'types/api'
import { useQueryClient } from '@tanstack/react-query'
import { yupResolver } from '@hookform/resolvers/yup'
import { Modal } from 'components/Modal/Modal'
import { useSnackbar } from 'stores/useSnackbar'
import { useLayout } from 'hooks/useLayout'
import {
  Box,
  Button,
  InputLabel,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
} from '@mui/material'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { formatDateTime } from 'utils/util'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage/FormFieldErrorMessage'
import { AdornedDateTimeField } from 'components/AdornedDateTimeField/AdornedDateTimeField'
import { SweepstakesStatusLabel } from 'components/Sweepstakes/SweepstakesStatusLabel'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { useGetSweepstakesByGroupId } from 'hooks/api/useGetSweepstakesByGroupId'
import { useDeleteSweepstakeById } from 'hooks/api/Sweepstake/useDeleteSweepstakeById'
import { usePutLocationSweepstake } from 'hooks/api/usePutLocationSweepstake'
import { SectionHeader } from 'components/SectionHeader'
import { ActivityButton } from 'components/ActivityButton'
import { isPresent } from '@jjvgaming/player-payback-library'
import { LocationSweepstakesDrawingSectionDeleteModal } from './LocationSweepstakeDrawingSectionDeleteModal'
import { LocationSweepstakesDrawingSectionAddModal } from './LocationSweepstakeDrawingSectionAddModal'

export const LocationSweepstakeDrawingsSection = ({
  sweepstake,
}: {
  sweepstake: Sweepstake
}) => {
  const { isMobile } = useLayout()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [drawingIdToDelete, setDrawingIdToDelete] = useState<number | null>(
    null
  )
  const [drawingNameToDelete, setDrawingNameToDelete] = useState<string | null>(
    null
  )
  const [drawingIdToEdit, setDrawingIdToEdit] = useState<number | null>(null)
  const [drawingNameToEdit, setDrawingNameToEdit] = useState<string | null>(
    null
  )
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const setMessage = useSnackbar((state) => state.setMessage)
  const drawingsQuery = useGetSweepstakesByGroupId(String(sweepstake.groupId))
  const deleteSweepstakeByIdMutation = useDeleteSweepstakeById({
    onSuccess: (_data, variables) => {
      setMessage(`Location Sweepstake successfully deleted`, 'success')
      toggleDeleteModalIsOpen()

      const deletedId = Number(variables.id)
      const remainingDrawings = (drawingsQuery.data ?? []).filter(
        (drawing) => drawing.id !== deletedId
      )

      if (deletedId !== sweepstake.id) {
        queryClient.removeQueries({ queryKey: ['/sweepstakes', deletedId] })
      }

      // Deleting the final drawing also deletes the parent sweepstake, so this page no longer exists.
      if (remainingDrawings.length === 0) {
        navigate('/Contests/LocationSweepstakes')
        return
      }

      void queryClient.invalidateQueries({
        queryKey: ['/contests/groupId', sweepstake.groupId],
      })

      // This page is keyed off a single drawing id, so deleting the drawing
      // being viewed requires redirecting to one that still exists.
      if (deletedId === sweepstake.id) {
        navigate(
          `/Contests/LocationSweepstakes/${remainingDrawings[0].id}/Overview`,
          { replace: true }
        )
      }
    },
    onError: () => {
      setMessage(
        `An error has occured while deleting the Location Sweepstake`,
        'error'
      )
    },
    invalidateSweepstakesQuery: false,
  })
  const putLocationSweepstake = usePutLocationSweepstake({
    ...sweepstake,
    id: drawingIdToEdit ?? 0,
    onSuccess: () => {
      setMessage('Sweepstake has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      toggleModalIsOpen()
    },
    onError: () => {
      setMessage('Error updating Sweepstake', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })
  const NEW_SWEEPSTAKE_DATA_ID = 0
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const getStartDate = (sweepstake: Sweepstake) => {
    if (isNil(sweepstake.startDate)) {
      return undefined
    }

    return parseISO(sweepstake.startDate)
  }

  const getEndDate = (sweepstake: Sweepstake) => {
    if (isNil(sweepstake.endDate)) {
      return undefined
    }

    return parseISO(sweepstake.endDate)
  }

  const drawings = drawingsQuery.data

  const getOrderedContestWinners = (drawing: Sweepstake) =>
    (drawing.contestWinners ?? [])
      .filter((x) => x.contestId === drawing.id)
      .sort((a, b) => (a.winnerNumber ?? 0) - (b.winnerNumber ?? 0))

  const SweepstakeDetailsSchema = object().shape({
    Sweepstake: object({
      state: string<SweepstakeStatusType>().required(
        'State is a required field'
      ),
      startDate: date().typeError('Must enter a valid start date'),
      endDate: date()
        .when(['startDate'], ([startDate], schema) => {
          if (isValid(startDate)) {
            return schema.min(
              startDate,
              'End date should not be before start date'
            )
          }
          return schema
        })
        .typeError('Must enter a valid end date'),
      numLocationWinners: number()
        .required('Must enter a number of winners per drawing')
        .transform((val, originalValue) =>
          originalValue === '' ? undefined : val
        )
        .min(1, 'Number of Winners must be greater than or equal to 1')
        .max(99, 'Number of Winners must be less than 100'),
      ContestWinner: array().of(
        object({
          winnerNumber: number().required(),
          cashPrize: boolean().nullable(),
          prizeType: string() ?? 'Prize',
          prizeAmount: number()
            .typeError('Prize Amount must be a number 0 or greater')
            .when('cashPrize', {
              is: true,
              then: (schema) =>
                schema
                  .min(0, 'Prize Amount must be 0 or greater')
                  .max(100000, 'Prize Amount cannot exceed 100,000'),
              otherwise: (schema) => schema.optional(),
            }),
          prizeDescription: string().required(
            'Prize Description is a required field'
          ),
        })
      ),
    }),
  })

  // Find the correct drawing by drawingIdToEdit
  const currentDrawing =
    drawings?.find((d) => d.id === drawingIdToEdit) ?? sweepstake

  const defaultNumLocationWinners = currentDrawing.numLocationWinners ?? 1

  const formMethods = useForm({
    resolver: yupResolver(SweepstakeDetailsSchema),
    mode: 'all',
    defaultValues: {
      Sweepstake: {
        startDate: getStartDate(currentDrawing),
        endDate: getEndDate(currentDrawing),
        numLocationWinners: defaultNumLocationWinners,
        state: currentDrawing.sweepstakeData?.state ?? 'Draft',
        ContestWinner: Array.from(
          { length: defaultNumLocationWinners },
          (_, i) => {
            const winner = getOrderedContestWinners(currentDrawing)[i]
            return {
              winnerNumber: winner?.winnerNumber ?? i + 1,
              cashPrize: winner?.cashPrize ?? false,
              prizeType:
                winner?.prizeType === 'Prize' ||
                winner?.prizeType === 'Cash' ||
                winner?.prizeType === 'Email'
                  ? winner?.prizeType
                  : 'Prize',
              prizeAmount: Number(winner?.prizeAmount ?? 0),
              prizeDescription: winner?.prizeDescription ?? '',
            }
          }
        ),
      },
    },
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = formMethods

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'Sweepstake.ContestWinner',
  })

  const watchedNumLocationWinners = watch('Sweepstake.numLocationWinners')
  const numLocationWinners = Math.max(
    1,
    Number(watchedNumLocationWinners ?? defaultNumLocationWinners)
  )

  // We use this to dynamically add/remove winner rows in the modal based on the number of winners selected
  // (previously we always had same amount of winners for every drawing but now it can be different per drawing)
  useEffect(() => {
    if (!isModalOpen) return
    const diff = numLocationWinners - fields.length
    if (diff > 0) {
      // Add new winner rows
      for (let i = 0; i < diff; i++) {
        append({
          winnerNumber: fields.length + i + 1,
          cashPrize: false,
          prizeAmount: 0,
          prizeDescription: '',
          prizeType: 'Prize',
        })
      }
    } else if (diff < 0) {
      // Remove extra winner rows
      for (let i = 0; i < Math.abs(diff); i++) {
        remove(fields.length - 1 - i)
      }
    }
  }, [numLocationWinners, isModalOpen])

  if (drawingsQuery.isPending) {
    return <ActivityIndicator />
  }

  if (drawingsQuery.isError || isNil(drawingsQuery.data)) {
    return <Box>An error occurred fetching the drawings.</Box>
  }

  const toggleModalIsOpen = () => {
    setIsModalOpen(!isModalOpen)
    reset()
  }
  const toggleDeleteModalIsOpen = () => setIsDeleteModalOpen(!isDeleteModalOpen)
  const toggleAddModalIsOpen = () => setIsAddModalOpen(!isAddModalOpen)

  const handleSubmitWrapper = handleSubmit((data) => {
    putLocationSweepstake.mutate({
      ...sweepstake,
      id: Number(drawingIdToEdit),
      startDate: formatISO(data.Sweepstake.startDate ?? new Date()),
      endDate: formatISO(data.Sweepstake.endDate ?? new Date()),
      numLocationWinners: data.Sweepstake.numLocationWinners,
      licensedEstablishmentIds: sweepstake?.licensedEstablishments?.map(
        (x) => x.id
      ),
      sweepstakeData: {
        contestId: Number(drawingIdToEdit),
        id: sweepstake.sweepstakeData?.id ?? NEW_SWEEPSTAKE_DATA_ID,
        state:
          drawings?.find((x) => x.id === drawingIdToEdit)?.sweepstakeData
            ?.state ?? 'Draft',
      },
      contestWinners: (data.Sweepstake.ContestWinner ?? []).map(
        (winner, index) => ({
          id: 0,
          groupId: String(sweepstake.groupId),
          contestId: Number(drawingIdToEdit),
          winnerNumber: Number(winner.winnerNumber ?? index + 1),
          cashPrize: winner.cashPrize ?? false,
          prizeType:
            winner?.prizeType === 'Prize' ||
            winner?.prizeType === 'Email' ||
            winner?.prizeType === 'Cash'
              ? winner?.prizeType
              : 'Prize',
          prizeAmount: Number(winner.prizeAmount ?? 0),
          prizeDescription: winner.prizeDescription ?? '',
        })
      ),
    })
  })

  return (
    <>
      <SectionHeader title="Drawings" showButton={false} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          rowGap: '16px',
          borderWidth: 1,
          borderColor: 'grey.300',
          borderRadius: 2,
          p: '16px',
          mb: '40px',
        }}
      >
        <Grid container spacing={3}>
          {drawings?.map((drawing, index) => (
            <Grid item xs={12} key={drawing.id}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <Typography variant="h6">
                  Drawing {Number(index) + 1}
                </Typography>
                {drawing.status && (
                  <SweepstakesStatusLabel
                    status={drawing.sweepstakeData?.state ?? 'Draft'}
                  />
                )}
                <Button
                  data-testid="drawing-edit-button"
                  variant="text"
                  size="small"
                  onClick={() => {
                    setValue('Sweepstake.startDate', getStartDate(drawing))
                    setValue('Sweepstake.endDate', getEndDate(drawing))
                    setValue(
                      'Sweepstake.numLocationWinners',
                      drawing.numLocationWinners ?? 1
                    )
                    setDrawingIdToEdit(Number(drawing.id))
                    setDrawingNameToEdit(`Drawing ${Number(index) + 1}`)
                    setIsModalOpen(true)
                    setValue(
                      'Sweepstake.ContestWinner',
                      Array.from(
                        { length: drawing.numLocationWinners ?? 1 },
                        (_, i) => {
                          const winner = getOrderedContestWinners(drawing)[i]
                          return {
                            winnerNumber: winner?.winnerNumber ?? i + 1,
                            cashPrize: winner?.cashPrize ?? false,
                            prizeType:
                              winner?.prizeType === 'Prize' ||
                              winner?.prizeType === 'Cash' ||
                              winner?.prizeType === 'Email'
                                ? winner?.prizeType
                                : 'Prize',
                            prizeAmount: Number(winner?.prizeAmount ?? 0),
                            prizeDescription: winner?.prizeDescription ?? '',
                          }
                        }
                      )
                    )
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    padding: '2px 6px',
                    marginLeft: '8px',
                    minWidth: 'auto',
                  }}
                >
                  Edit
                </Button>
                <Button
                  data-testid="drawing-delete-button"
                  variant="text"
                  size="small"
                  disabled={
                    drawing.sweepstakeData?.state === 'WinnerValidation' ||
                    drawing.sweepstakeData?.state === 'InternalValidation' ||
                    drawing.sweepstakeData?.state === 'Closed'
                  }
                  onClick={() => {
                    setDrawingIdToDelete(drawing.id)
                    setDrawingNameToDelete(`Drawing ${Number(index) + 1}`)
                    setIsDeleteModalOpen(true)
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    padding: '2px 6px',
                    marginLeft: '8px',
                    minWidth: 'auto',
                  }}
                >
                  Delete
                </Button>
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>Start Date:</strong>{' '}
                    {formatDateTime(drawing.startDate, 'P p')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>End Date:</strong>{' '}
                    {formatDateTime(drawing.endDate, 'P p')}
                  </Typography>
                </Grid>

                {drawing.contestWinners?.length === 0 && (
                  // If there are no winners, show Winner #1 with no details
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      <strong>Winner #1: </strong>{' '}
                    </Typography>
                  </Grid>
                )}
                {drawing.contestWinners?.length !== 0 &&
                  Array.from(
                    { length: drawing.numLocationWinners ?? 1 },
                    (_, i) => {
                      const winner = getOrderedContestWinners(drawing)[i]
                      return (
                        <Grid item xs={12} key={i}>
                          <Typography variant="body1">
                            <strong>Winner #{i + 1}: </strong>{' '}
                            {`${String(winner?.prizeDescription)}`}
                            {Number(winner?.prizeAmount) !== 0
                              ? ` - $${Number(winner?.prizeAmount ?? 0).toFixed(2)}`
                              : ''}
                          </Typography>
                        </Grid>
                      )
                    }
                  )}
              </Grid>
            </Grid>
          ))}
          <Button
            data-testid="drawing-add-button"
            variant="contained"
            size="small"
            onClick={() => {
              setIsAddModalOpen(true)
            }}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              padding: '2px 6px',
              marginTop: 2,
              marginLeft: 3,
              minWidth: 'auto',
            }}
          >
            Add Drawing
          </Button>
        </Grid>
      </Box>
      <LocationSweepstakesDrawingSectionDeleteModal
        isModalOpen={isDeleteModalOpen}
        toggleIsOpen={toggleDeleteModalIsOpen}
        onCancel={toggleDeleteModalIsOpen}
        deleteIsActive={deleteSweepstakeByIdMutation.isPending}
        onConfirm={() =>
          deleteSweepstakeByIdMutation.mutate({ id: drawingIdToDelete })
        }
        drawingName={drawingNameToDelete ?? ''}
      />
      <LocationSweepstakesDrawingSectionAddModal
        isModalOpen={isAddModalOpen}
        toggleIsOpen={toggleAddModalIsOpen}
        onCancel={toggleAddModalIsOpen}
        onConfirm={toggleAddModalIsOpen}
        sweepstake={sweepstake}
      />
      <Modal
        isOpen={isModalOpen}
        toggleIsOpen={() => {}}
        sx={{
          width: isMobile ? '100%' : '50%',
          height: isMobile ? '100%' : '75%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <form
            onSubmit={handleSubmitWrapper}
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <ModalFullHeader
              toggleIsOpen={toggleModalIsOpen}
              title={drawingNameToEdit ?? ''}
            />
            <ModalFullBody
              heightModal={'75%'}
              sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
            >
              <Grid
                item
                container
                alignItems={'center'}
                xs={12}
                md={12}
                sx={{ marginBottom: 2 }}
              >
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    htmlFor="Sweepstake.startDate"
                    sx={{ fontWeight: 'normal', margin: 0 }}
                  >
                    Start Date/Time
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <AdornedDateTimeField
                    name="Sweepstake.startDate"
                    control={control}
                  />
                  {errors.Sweepstake?.startDate?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake?.startDate.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid item container alignItems={'center'} xs={12} md={12}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    htmlFor="Sweepstake.endDate"
                    sx={{ fontWeight: 'normal', margin: 0 }}
                  >
                    End Date/Time
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <AdornedDateTimeField
                    name="Sweepstake.endDate"
                    control={control}
                  />
                  {errors.Sweepstake?.endDate?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake?.endDate.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid item container alignItems={'center'} xs={12} md={12} mt={2}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    htmlFor="Sweepstake.numLocationWinners"
                    sx={{ fontWeight: 'normal', margin: 0 }}
                  >
                    Number of Winners
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    type="number"
                    {...register('Sweepstake.numLocationWinners', {
                      valueAsNumber: true,
                      required: true,
                    })}
                    disabled={
                      currentDrawing.sweepstakeData?.state ===
                        'WinnerValidation' ||
                      currentDrawing.sweepstakeData?.state ===
                        'InternalValidation' ||
                      currentDrawing.sweepstakeData?.state === 'Closed'
                    }
                    error={isPresent(errors.Sweepstake?.numLocationWinners)}
                    size="small"
                    inputProps={{ min: 1, max: 99 }}
                    sx={{ width: '100%' }}
                  />
                  {errors.Sweepstake?.numLocationWinners?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake.numLocationWinners.message}
                    />
                  )}
                </Grid>
              </Grid>

              {fields.map((field, index) => (
                <Grid
                  container
                  spacing={2}
                  marginTop={1}
                  borderTop={1}
                  key={field.id}
                >
                  <Grid item xs={12} md={2}>
                    <InputLabel sx={{ fontWeight: 'normal', margin: 1 }}>
                      Winner #{index + 1}
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <InputLabel sx={{ fontWeight: 'normal', margin: 0 }}>
                      Prize Type
                    </InputLabel>
                    <Select
                      style={{ height: '40px' }}
                      placeholder="Prize"
                      {...register(
                        `Sweepstake.ContestWinner.${index}.prizeType`
                      )}
                      defaultValue={field.prizeType ?? 'Prize'}
                    >
                      <MenuItem value="Prize">Prize</MenuItem>
                      <MenuItem value="Cash">Cash</MenuItem>
                      <MenuItem value="Email">Email</MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel sx={{ fontWeight: 'normal', margin: 0 }}>
                      Prize Description
                    </InputLabel>
                    <TextField
                      fullWidth
                      {...register(
                        `Sweepstake.ContestWinner.${index}.prizeDescription`
                      )}
                      defaultValue={field.prizeDescription ?? ''}
                      error={isPresent(
                        errors.Sweepstake?.ContestWinner?.[index]
                          ?.prizeDescription
                      )}
                      size="small"
                      inputProps={{ maxLength: 50 }}
                    />
                    {errors.Sweepstake?.ContestWinner?.[index]?.prizeDescription
                      ?.message && (
                      <FormFieldErrorMessage
                        message={
                          errors.Sweepstake?.ContestWinner?.[index]
                            ?.prizeDescription.message
                        }
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <InputLabel sx={{ fontWeight: 'normal', margin: 0 }}>
                      Prize Amount
                    </InputLabel>
                    <TextField
                      type="number"
                      {...register(
                        `Sweepstake.ContestWinner.${index}.prizeAmount`
                      )}
                      defaultValue={Number(field.prizeAmount ?? 0)}
                      error={isPresent(
                        errors.Sweepstake?.ContestWinner?.[index]?.prizeAmount
                      )}
                      size="small"
                      inputProps={{ min: 0, max: 100000.0 }}
                      sx={{ width: '100%' }}
                    />
                    {errors.Sweepstake?.ContestWinner?.[index]?.prizeAmount
                      ?.message && (
                      <FormFieldErrorMessage
                        message={
                          errors.Sweepstake?.ContestWinner?.[index]?.prizeAmount
                            .message
                        }
                      />
                    )}
                  </Grid>
                </Grid>
              ))}
            </ModalFullBody>
            <ModalFullFooter>
              <Button onClick={toggleModalIsOpen} sx={{ marginRight: 1 }}>
                Cancel
              </Button>
              <ActivityButton
                type="submit"
                variant="contained"
                active={putLocationSweepstake.isPending}
              >
                Save
              </ActivityButton>
            </ModalFullFooter>
          </form>
        </Box>
      </Modal>
    </>
  )
}
